import { Component, Input } from '@angular/core';
import { FormBuilder, FormControl, FormArray } from '@angular/forms';
import { CDFTService } from 'app/modules/cdft/services/cdft.service';
import { CDFTState } from 'app/modules/cdft/state/cdft.state';
import { Subscription } from 'rxjs';
import { each } from 'lodash';
import module from 'app/modules/cdft/cdft.module';

@Component({
    selector: 'control-line',
    templateUrl: './control-line.component.html',
    standalone: true,
    styleUrls: ['./control-line.component.scss'],
    imports: module,
})
export class ControlLineComponent {
    @Input() isViewOnly: boolean;
    formGroup: FormArray;
    private subscriptions: Subscription = new Subscription();

    constructor(
        private formBuilder: FormBuilder,
        private state: CDFTState,
        public service: CDFTService
    ) { }

    get primaryFormGroup() {
        const index =
            this.formGroup.at(0).getRawValue().controlLineType == 'Primary'
                ? 0
                : 1;
        return this.formGroup.at(index);
    }

    get secondaryFormGroup() {
        const index =
            this.formGroup.at(0).getRawValue().controlLineType == 'Secondary'
                ? 0
                : 1;
        return this.formGroup.at(index);
    }

    ngOnInit() {
        this.formGroup = this.onFormGroup();
        this.state.setFormGroup('cdftControlLineTest', this.formGroup);

        this.onPopulate();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    onValveTested() {
        const valveBehaviour = (formGroup, valveTested, fields) => {
            each(fields, (element) => {
                formGroup.get(element).disable();
            });

            // if (!(valveTested == 'Yes' || valveTested == 'UTT')) return;

            if (valveTested === 'UTT') {
                ['hydraulicReturn', 'blowingGas'].forEach(f => formGroup.get(f).enable());

                if (fields.includes('svlnInstalled')) {
                    formGroup.get('svlnInstalled').enable();
                }
            } else if (valveTested === 'NA') {
                ['hydraulicReturn', 'blowingGas'].forEach(f => formGroup.get(f).setValue(null));

                if (fields.includes('svlnInstalled')) {
                    formGroup.get('svlnInstalled').setValue(null);
                }
            }

            fields = fields.filter((field) => {
                return valveTested == 'Yes'
                    ? field != 'remarks'
                    : field == 'remarks';
            });

            each(fields, (field) => {
                formGroup.get(field).enable();
            });
        };

        if (this.primaryFormGroup.get('valveTest').value == 'UTT') {
            let valvedTest = this.primaryFormGroup.get('valveTest').value
            valveBehaviour(this.primaryFormGroup, valvedTest, [
                'remarks',
                'clInitialPressure',
                'clTestedPressure',
                'clPressureAfterTest',
                'hydraulicReturn',
                'blowingGas',
                'periodOfTesting',
                // 'clTestedResult',
            ]);
        }

        if (this.secondaryFormGroup.get('valveTest').value == 'UTT') {
            let valvedTest = this.secondaryFormGroup.get('valveTest').value
            valveBehaviour(this.secondaryFormGroup, valvedTest, [
                'remarks',
                'svlnInstalled',
                'clInitialPressure',
                'clTestedPressure',
                'clPressureAfterTest',
                'hydraulicReturn',
                'blowingGas',
                'periodOfTesting',
                // 'clTestedResult',
            ]);
        }

        const subscriptionPrimary = this.primaryFormGroup
            .get('valveTest')
            .valueChanges.subscribe((valvedTest) => {
                valveBehaviour(this.primaryFormGroup, valvedTest, [
                    'remarks',
                    'clInitialPressure',
                    'clTestedPressure',
                    'clPressureAfterTest',
                    'hydraulicReturn',
                    'blowingGas',
                    'periodOfTesting',
                    // 'clTestedResult',
                ]);

                if (valvedTest == 'UTT') {
                    this.primaryFormGroup.get('clTestedResult').setValue('UTT')
                } else if (valvedTest == 'NA') {
                    this.primaryFormGroup.get('clTestedResult').setValue('NA')
                }
            });

        const subscriptionSecondary = this.secondaryFormGroup
            .get('valveTest')
            .valueChanges.subscribe((valvedTest) => {
                valveBehaviour(this.secondaryFormGroup, valvedTest, [
                    'remarks',
                    'svlnInstalled',
                    'clInitialPressure',
                    'clTestedPressure',
                    'clPressureAfterTest',
                    'hydraulicReturn',
                    'blowingGas',
                    'periodOfTesting',
                    // 'clTestedResult',
                ]);

                if (valvedTest == 'UTT') {
                    this.secondaryFormGroup.get('clTestedResult').setValue('UTT')
                } else if (valvedTest == 'NA') {
                    this.secondaryFormGroup.get('clTestedResult').setValue('NA')
                }
            });

        this.subscriptions.add(subscriptionPrimary);
        this.subscriptions.add(subscriptionSecondary);
    }

    onPopulate() {
        const subscription = this.state.subscribe((cdft: CDFTState) => {
            if (!cdft.cdftControlLineTest) return;

            this.formGroup.patchValue(cdft.cdftControlLineTest);
            this.onValveTested();

        });

        this.subscriptions.add(subscription);
    }

    onFormGroup() {
        return this.formBuilder.array([
            this.formBuilder.group({
                id: new FormControl({ value: null, disabled: true }),
                cdftRecordId: new FormControl({ value: null, disabled: true }),
                controlLineType: new FormControl({
                    value: 'Primary',
                    disabled: true,
                }),
                valveTest: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                remarks: new FormControl({ value: null, disabled: true }),
                clInitialPressure: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                clTestedPressure: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                clPressureAfterTest: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                hydraulicReturn: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                blowingGas: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                periodOfTesting: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                svlnInstalled: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                clTestedResult: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
            }),
            this.formBuilder.group({
                id: new FormControl({ value: null, disabled: true }),
                cdftRecordId: new FormControl({ value: null, disabled: true }),
                controlLineType: new FormControl({
                    value: 'Secondary',
                    disabled: true,
                }),
                valveTest: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                remarks: new FormControl({ value: null, disabled: true }),
                clInitialPressure: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                clTestedPressure: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                clPressureAfterTest: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                hydraulicReturn: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                blowingGas: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                periodOfTesting: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                svlnInstalled: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
                clTestedResult: new FormControl({
                    value: null,
                    disabled: this.isViewOnly,
                }),
            }),
        ]);
    }
}
